import React, { forwardRef, useState, useRef, useEffect } from 'react';
import styled, { css } from 'styled-components';
import colors from '../../colors';
import typography from '../../typography';
import spacingSystem from '../../spacing';

// Select sizes
export type SelectSize = 'small' | 'medium' | 'large';

// Select variants
export type SelectVariant = 'default' | 'filled' | 'outlined';

// Option type
export interface SelectOption {
  /**
   * The value of the option
   */
  value: string | number;
  
  /**
   * The label of the option
   */
  label: string;
  
  /**
   * Whether the option is disabled
   */
  disabled?: boolean;
}

// Select props
export interface SelectProps {
  /**
   * The options of the select
   */
  options: SelectOption[];
  
  /**
   * The value of the select
   */
  value?: string | number;
  
  /**
   * The default value of the select
   */
  defaultValue?: string | number;
  
  /**
   * The placeholder of the select
   */
  placeholder?: string;
  
  /**
   * The size of the select
   * @default 'medium'
   */
  size?: SelectSize;
  
  /**
   * The variant of the select
   * @default 'default'
   */
  variant?: SelectVariant;
  
  /**
   * Whether the select is disabled
   * @default false
   */
  disabled?: boolean;
  
  /**
   * Whether the select is in an error state
   * @default false
   */
  error?: boolean;
  
  /**
   * Error message to display
   */
  errorMessage?: string;
  
  /**
   * Label for the select
   */
  label?: string;
  
  /**
   * Helper text to display below the select
   */
  helperText?: string;
  
  /**
   * Whether the select should take the full width of its container
   * @default false
   */
  fullWidth?: boolean;
  
  /**
   * Callback when the value changes
   */
  onChange?: (value: string | number) => void;
  
  /**
   * Callback when the select receives focus
   */
  onFocus?: () => void;
  
  /**
   * Additional class name
   */
  className?: string;
  
  /**
   * The name of the select
   */
  name?: string;
  
  /**
   * Whether the select is required
   */
  required?: boolean;
  
  /**
   * The id of the select
   */
  id?: string;
}

// Size styles
const getSizeStyles = (size: SelectSize) => {
  switch (size) {
    case 'small':
      return css`
        padding: ${spacingSystem.spacing[4]} ${spacingSystem.spacing[8]};
        font-size: ${typography.fontSize.sm};
        height: 32px;
      `;
    case 'medium':
      return css`
        padding: ${spacingSystem.spacing[8]} ${spacingSystem.spacing[12]};
        font-size: ${typography.fontSize.md};
        height: 40px;
      `;
    case 'large':
      return css`
        padding: ${spacingSystem.spacing[12]} ${spacingSystem.spacing[16]};
        font-size: ${typography.fontSize.lg};
        height: 48px;
      `;
    default:
      return '';
  }
};

// Variant styles
const getVariantStyles = (variant: SelectVariant, error: boolean) => {
  const errorColor = colors.semantic.error.default;
  const borderColor = error ? errorColor : colors.neutral[300];
  const focusBorderColor = error ? errorColor : colors.primary[500];
  
  switch (variant) {
    case 'default':
      return css`
        background-color: white;
        border: 1px solid ${borderColor};
        
        &:hover:not(:disabled) {
          border-color: ${error ? errorColor : colors.neutral[400]};
        }
        
        &:focus {
          border-color: ${focusBorderColor};
          box-shadow: 0 0 0 3px ${error ? colors.semantic.error.light : colors.primary[100]};
        }
      `;
    case 'filled':
      return css`
        background-color: ${colors.neutral[100]};
        border: 1px solid transparent;
        
        &:hover:not(:disabled) {
          background-color: ${colors.neutral[200]};
        }
        
        &:focus {
          background-color: white;
          border-color: ${focusBorderColor};
          box-shadow: 0 0 0 3px ${error ? colors.semantic.error.light : colors.primary[100]};
        }
      `;
    case 'outlined':
      return css`
        background-color: transparent;
        border: 2px solid ${borderColor};
        
        &:hover:not(:disabled) {
          border-color: ${error ? errorColor : colors.neutral[400]};
        }
        
        &:focus {
          border-color: ${focusBorderColor};
          box-shadow: 0 0 0 2px ${error ? colors.semantic.error.light : colors.primary[100]};
        }
      `;
    default:
      return '';
  }
};

const SelectContainer = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${props => props.fullWidth ? '100%' : 'auto'};
  position: relative;
`;

const Label = styled.label<{ error?: boolean }>`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: ${props => props.error ? colors.semantic.error.default : colors.neutral[800]};
  margin-bottom: ${spacingSystem.spacing[4]};
`;

const SelectWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  z-index: auto;
`;

const StyledSelect = styled.div<{
  $size: SelectSize;
  $variant: SelectVariant;
  $error?: boolean;
  $disabled?: boolean;
  $fullWidth?: boolean;
  $isOpen?: boolean;
}>`
  /* Base styles */
  font-family: ${typography.fontFamily.primary};
  font-weight: ${typography.fontWeight.regular};
  color: ${colors.neutral[900]};
  border-radius: 4px;
  width: 100%;
  outline: none;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  /* Size styles */
  ${props => getSizeStyles(props.$size)}
  
  /* Variant styles */
  ${props => getVariantStyles(props.$variant, Boolean(props.$error))}
  
  /* Disabled state */
  ${props => props.$disabled && css`
    opacity: 0.5;
    cursor: not-allowed;
    background-color: ${colors.neutral[100]};
    border-color: ${colors.neutral[200]};
  `}
  
  /* Open state */
  ${props => props.$isOpen && css`
    border-color: ${props.$error ? colors.semantic.error.default : colors.primary[500]};
    box-shadow: 0 0 0 3px ${props.$error ? colors.semantic.error.light : colors.primary[100]};
  `}
`;

const SelectValue = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Placeholder = styled.div`
  color: ${colors.neutral[400]};
`;

const ChevronIcon = styled.div<{ isOpen?: boolean }>`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  
  &::before {
    content: '';
    display: block;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid ${colors.neutral[600]};
  }
`;

const DropdownContainer = styled.div<{ width: number }>`
  position: absolute;
  top: 100%;
  left: 0;
  width: ${props => props.width}px;
  max-height: 200px;
  overflow-y: auto;
  background-color: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: 4px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 9999;
  margin-top: 4px;
  pointer-events: auto;
`;

const OptionsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const OptionItem = styled.li<{ isSelected?: boolean; isDisabled?: boolean }>`
  padding: ${spacingSystem.spacing[8]} ${spacingSystem.spacing[12]};
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  ${props => props.isSelected && css`
    background-color: ${colors.primary[100]};
    color: ${colors.primary[700]};
    font-weight: ${typography.fontWeight.medium};
  `}
  
  ${props => !props.isSelected && css`
    &:hover {
      background-color: ${colors.neutral[100]};
    }
  `}
  
  ${props => props.isDisabled && css`
    opacity: 0.5;
    cursor: not-allowed;
    
    &:hover {
      background-color: transparent;
    }
  `}
`;

const HelperText = styled.p<{ error?: boolean }>`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.xs};
  color: ${props => props.error ? colors.semantic.error.default : colors.neutral[600]};
  margin-top: ${spacingSystem.spacing[4]};
  margin-bottom: 0;
`;

/**
 * Select component for dropdown selection
 */
export const Select = forwardRef<HTMLDivElement, SelectProps>(({
  options = [],
  value,
  defaultValue,
  placeholder = 'Select an option',
  size = 'medium',
  variant = 'default',
  disabled = false,
  error = false,
  errorMessage,
  label,
  helperText,
  fullWidth = false,
  onChange,
  onFocus,
  className,
  name,
  required,
  id: propId,
  ...rest
}, ref) => {
  // Removed excessive console logging to prevent re-render spam
  
  // Generate a unique ID for the select
  const uniqueId = useRef(`select-${Math.random().toString(36).substr(2, 9)}`);
  const id = propId || uniqueId.current;
  
  // Determine if this is a controlled component
  const isControlled = value !== undefined;
  
  // State for uncontrolled component only
  const [internalValue, setInternalValue] = useState<string | number | undefined>(defaultValue);
  
  // Use either the controlled value or internal state
  const selectedValue = isControlled ? value : internalValue;
  
  // State for dropdown open/close
  const [isOpen, setIsOpen] = useState(false);
  
  // Ref for measuring the select width - use MutableRefObject instead of RefObject
  const selectRef = useRef<HTMLDivElement | null>(null) as React.MutableRefObject<HTMLDivElement | null>;
  const [selectWidth, setSelectWidth] = useState(0);
  
  // Measure select width when it mounts or resizes
  useEffect(() => {
    if (selectRef.current) {
      setSelectWidth(selectRef.current.offsetWidth);
    }
    
    const handleResize = () => {
      if (selectRef.current) {
        setSelectWidth(selectRef.current.offsetWidth);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Handle option selection
  const handleOptionSelect = (option: SelectOption) => {
    // Add defensive check for undefined option
    if (!option) {
      return;
    }

    if (option.disabled) return;
    
    // For uncontrolled components, update internal state
    if (!isControlled) {
      setInternalValue(option.value);
    }
    
    setIsOpen(false);
    
    // Always call onChange if provided
    if (onChange && option.value !== undefined) {
      onChange(option.value);
    }
  };
  
  // Toggle dropdown
  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      // Call onFocus when opening dropdown if provided
      if (!isOpen && onFocus) {
        onFocus();
      }
    }
  };
  
  // Find selected option
  const selectedOption = options.find(option => option.value === selectedValue);
  
  return (
    <SelectContainer fullWidth={fullWidth} className={className}>
      {label && (
        <Label htmlFor={id} error={error}>
          {label}{required && <span style={{ color: colors.semantic.error.default }}> *</span>}
        </Label>
      )}
      
      <SelectWrapper>
        <StyledSelect
          ref={(node) => {
            // Forward the ref
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              // Use type assertion to safely assign to ref.current
              (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
            }
            
            // Also keep our local ref
            selectRef.current = node;
          }}
          $size={size}
          $variant={variant}
          $error={error}
          $disabled={disabled}
          $fullWidth={fullWidth}
          $isOpen={isOpen}
          onClick={toggleDropdown}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={`${id}-listbox`}
          aria-labelledby={label ? `${id}-label` : undefined}
          aria-invalid={error}
          aria-describedby={errorMessage || helperText ? `${id}-helper-text` : undefined}
          tabIndex={disabled ? -1 : 0}
          {...rest}
        >
          {selectedOption ? (
            <SelectValue>{selectedOption.label}</SelectValue>
          ) : (
            <Placeholder>{placeholder}</Placeholder>
          )}
          <ChevronIcon isOpen={isOpen} />
        </StyledSelect>
      </SelectWrapper>
      
      {isOpen && (
        <DropdownContainer width={selectWidth}>
          <OptionsList role="listbox" id={`${id}-listbox`}>
            {options && options.length > 0 ? (
              options.map((option, index) => (
              <OptionItem
                key={index}
                isSelected={option.value === selectedValue}
                isDisabled={option.disabled}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleOptionSelect(option);
                }}
                role="option"
                aria-selected={option.value === selectedValue}
                aria-disabled={option.disabled}
              >
                {option.label}
              </OptionItem>
            ))) : (
              <div style={{ padding: '8px', color: '#999' }}>No options available</div>
            )}
          </OptionsList>
        </DropdownContainer>
      )}
      
      {(errorMessage || helperText) && (
        <HelperText id={`${id}-helper-text`} error={error}>
          {error && errorMessage ? errorMessage : helperText}
        </HelperText>
      )}
      
      {/* Hidden native select for form submission */}
      <input
        type="hidden"
        name={name}
        value={selectedValue !== undefined ? String(selectedValue) : ''}
        aria-hidden="true"
      />
    </SelectContainer>
  );
});

Select.displayName = 'Select';

// Memoize the Select component to prevent unnecessary re-renders
// Only re-render if props actually change
export default React.memo(Select, (prevProps, nextProps) => {
  // Handle undefined options
  if (!prevProps.options || !nextProps.options) {
    return prevProps.options === nextProps.options;
  }

  // Deep comparison for options array to prevent re-renders from recreated arrays
  const optionsEqual = prevProps.options.length === nextProps.options.length &&
    prevProps.options.every((opt, idx) => {
      const nextOpt = nextProps.options[idx];
      return opt.value === nextOpt.value &&
             opt.label === nextOpt.label &&
             opt.disabled === nextOpt.disabled;
    });
  
  return (
    prevProps.value === nextProps.value &&
    prevProps.defaultValue === nextProps.defaultValue &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.error === nextProps.error &&
    prevProps.placeholder === nextProps.placeholder &&
    prevProps.size === nextProps.size &&
    prevProps.variant === nextProps.variant &&
    prevProps.fullWidth === nextProps.fullWidth &&
    prevProps.label === nextProps.label &&
    prevProps.helperText === nextProps.helperText &&
    prevProps.errorMessage === nextProps.errorMessage &&
    optionsEqual // Deep comparison for options
    // Removed onChange comparison - callbacks shouldn't prevent re-renders if other props change
  );
});